import { useState } from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';

import { SearchMajorsFilters } from '../types';

interface SortOption {
  value: SearchMajorsFilters['sort'];
  label: string;
  order: 'asc' | 'desc';
}

/** Base sort options (Relevance is added conditionally when search is active) */
const BASE_SORT_OPTIONS: SortOption[] = [
  { value: 'name', label: 'Name', order: 'asc' },
  { value: 'underemployment', label: 'Under-employment rate', order: 'desc' },
  { value: 'rigor', label: 'Rigor', order: 'desc' },
  { value: 'demand', label: 'Market Demand', order: 'desc' },
  { value: 'optionValue', label: 'Option Value', order: 'desc' },
];

const RELEVANCE_OPTION: SortOption = { value: 'relevance', label: 'Relevance', order: 'desc' };

/**
 * Props for the FilterControls component
 */
export interface FilterControlsProps {
  /** Current search input value */
  searchValue: string;
  /** Callback when search value changes */
  onSearchChange: (value: string) => void;
  /** Current college filter values (supports multiple selection) */
  collegeValues: string[];
  /** Callback when college filter changes */
  onCollegeChange: (values: string[]) => void;
  /** Current sort field */
  sortValue: SearchMajorsFilters['sort'];
  /** Current sort order */
  orderValue: 'asc' | 'desc';
  /** Callback when sort field changes */
  onSortChange: (value: SearchMajorsFilters['sort'], order: 'asc' | 'desc') => void;
  /** Available college options for dropdown */
  collegeOptions?: string[];
  /** Callback when Search button is clicked */
  onSearch?: () => void;
  /** Callback when Clear button is clicked */
  onClear?: () => void;
  /** Whether an active search term has been applied (controls Relevance sort visibility) */
  hasActiveSearch?: boolean;
}

/**
 * FilterControls component - Provides filtering and sorting controls for the explore page
 *
 * Features:
 * - Search bar container with label, input, Clear and Search buttons
 * - Filter row with college dropdown (multi-select), filter chips, and sort toggle
 * - Matches Figma design prototype styling
 */
export const FilterControls = ({
  searchValue,
  onSearchChange,
  collegeValues,
  onCollegeChange,
  sortValue,
  orderValue,
  onSortChange,
  collegeOptions = [],
  onSearch,
  onClear,
  hasActiveSearch = false,
}: FilterControlsProps) => {
  const [collegeDropdownOpen, setCollegeDropdownOpen] = useState(false);

  const sortOptions = hasActiveSearch
    ? [RELEVANCE_OPTION, ...BASE_SORT_OPTIONS]
    : BASE_SORT_OPTIONS;
  const effectiveSortValue = sortOptions.some((o) => o.value === sortValue)
    ? sortValue
    : sortOptions[0].value;

  const handleCollegeItemClick = (college: string) => {
    if (collegeValues.includes(college)) {
      // Remove from selection
      onCollegeChange(collegeValues.filter((c) => c !== college));
    } else {
      // Add to selection
      onCollegeChange([...collegeValues, college]);
    }
  };

  const handleRemoveCollegeChip = (college: string) => {
    onCollegeChange(collegeValues.filter((c) => c !== college));
  };

  const handleSearchEnterKey = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      onSearch?.();
    }
  };

  const hasSelectedColleges = collegeValues.length > 0;

  return (
    <Box sx={outerContainerSx}>
      {/* Search Bar Container */}
      <Box sx={searchBarContainerSx}>
        <TextField
          id="majors-guide-search-input"
          label="Search Majors"
          variant="outlined"
          placeholder="Search by name..."
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          onKeyDown={handleSearchEnterKey}
          InputProps={{
            startAdornment: <AppIcon iconName="SearchMd" color="currentColor" diameter="20px" />,
          }}
          sx={searchInputSx}
        />
        <Box sx={searchButtonContainerSx}>
          <Button
            id="majors-guide-clear-button"
            hierarchy="Secondary gray"
            size="lg"
            onClick={onClear}
          >
            Clear
          </Button>
          <Button id="majors-guide-search-button" hierarchy="Primary" size="lg" onClick={onSearch}>
            Search
          </Button>
        </Box>
      </Box>

      {/* Filter Row */}
      <Box sx={filterRowSx}>
        {/* Left side: College filter dropdown + chips */}
        <Box sx={filterLeftSx}>
          {/* College Filter Dropdown */}
          <Select
            id="majors-guide-college-filter-select"
            multiple
            value={collegeValues}
            onOpen={() => setCollegeDropdownOpen(true)}
            onClose={() => setCollegeDropdownOpen(false)}
            open={collegeDropdownOpen}
            displayEmpty
            onChange={() => null} // We handle selection via MenuItem onClick
            MenuProps={{
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
              PaperProps: {
                sx: menuPaperPropsSx,
              },
            }}
            renderValue={() => (
              <Box sx={dropdownRenderValueSx}>
                <AppIcon
                  iconName="Building07"
                  diameter="20px"
                  color={
                    hasSelectedColleges
                      ? 'Colors/Text/text-primary'
                      : 'Colors/Text/text-placeholder'
                  }
                />
                <Typography
                  variant="text-md-medium"
                  sx={({ backrs }) => ({
                    color: hasSelectedColleges
                      ? backrs.colors['Colors/Text/text-primary']
                      : backrs.colors['Colors/Text/text-placeholder'],
                  })}
                >
                  Typical College
                </Typography>
                <AppIcon
                  iconName={collegeDropdownOpen ? 'ChevronUp' : 'ChevronDown'}
                  diameter="20px"
                  color={
                    hasSelectedColleges
                      ? 'Colors/Text/text-primary'
                      : 'Colors/Text/text-placeholder'
                  }
                />
              </Box>
            )}
            sx={collegeDropdownSx}
          >
            {collegeOptions.map((college) => {
              const isSelected = collegeValues.includes(college);

              return (
                <MenuItem
                  key={college}
                  value={college}
                  onClick={() => handleCollegeItemClick(college)}
                  disableRipple
                >
                  <Box sx={menuItemContentSx}>
                    <Box sx={menuItemCheckboxSx(isSelected)}>
                      <AppIcon
                        iconName={isSelected ? 'Check' : 'Square'}
                        diameter="16px"
                        color={isSelected ? 'Colors/Foreground/fg-white' : 'currentColor'}
                      />
                    </Box>
                    <Typography variant="text-md-medium">{college}</Typography>
                  </Box>
                </MenuItem>
              );
            })}
          </Select>

          {/* Selected Filter Chips */}
          {collegeValues.map((college) => (
            <Box key={college} sx={filterChipSx} onClick={() => handleRemoveCollegeChip(college)}>
              <AppIcon iconName="XClose" diameter="16px" color="Colors/Text/text-secondary" />
              <Typography variant="text-sm-medium" sx={filterChipTextSx}>
                {college}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Right side: Sort By dropdown + Order toggle */}
        <Box sx={sortControlsContainerSx}>
          <Select
            id="majors-guide-sort-select"
            value={effectiveSortValue}
            onChange={(e) => {
              const selected = sortOptions.find((opt) => opt.value === e.target.value);

              if (selected) {
                onSortChange(selected.value, selected.order);
              }
            }}
            displayEmpty
            MenuProps={{
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
              transformOrigin: { vertical: 'top', horizontal: 'left' },
              PaperProps: { sx: menuPaperPropsSx },
            }}
            renderValue={() => (
              <Box sx={sortByRenderValueSx}>
                <AppIcon iconName="FilterLines" diameter="20px" color="Colors/Text/text-primary" />
                <Typography variant="text-md-medium" sx={sortByTextSx}>
                  Sort By
                </Typography>
                <AppIcon iconName="ChevronDown" diameter="20px" color="Colors/Text/text-primary" />
              </Box>
            )}
            sx={sortByDropdownSx}
          >
            {sortOptions.map((option) => {
              const isSelected = effectiveSortValue === option.value;

              return (
                <MenuItem key={option.value} value={option.value} disableRipple>
                  <Box sx={sortMenuItemContentSx}>
                    <Box sx={sortMenuItemCheckboxSx(isSelected)}>
                      <AppIcon
                        iconName={isSelected ? 'Check' : 'Square'}
                        diameter="16px"
                        color={isSelected ? 'Colors/Foreground/fg-white' : 'currentColor'}
                      />
                    </Box>
                    <Typography variant="text-md-medium">{option.label}</Typography>
                  </Box>
                </MenuItem>
              );
            })}
          </Select>

          {/* Order Toggle (Segmented Control) */}
          <Box sx={orderToggleContainerSx}>
            <Box
              id="majors-guide-sort-asc-button"
              sx={orderToggleButtonSx(orderValue === 'asc')}
              onClick={() => onSortChange(sortValue, 'asc')}
            >
              <Typography variant="text-sm-medium">Asc</Typography>
            </Box>
            <Box
              id="majors-guide-sort-desc-button"
              sx={orderToggleButtonSx(orderValue === 'desc')}
              onClick={() => onSortChange(sortValue, 'desc')}
            >
              <Typography variant="text-sm-medium">Desc</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// Styles

const outerContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
});

const searchBarContainerSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  borderRadius: backrs.radius['radius-xl'],
  padding: backrs.spacing['spacing-2xl'],
  paddingTop: backrs.spacing['spacing-5xl'],
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xl'],
});

const searchInputSx: SxProps<Theme> = ({ backrs }) => ({
  flex: '1 1 min(300px, 100%)',
  minWidth: 0,
  '& .MuiOutlinedInput-root': {
    minHeight: '48px',
    border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
    backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  },
  '& .MuiOutlinedInput-root.Mui-focused': {
    border: `1px solid ${backrs.colors['Colors/Border/border-brand']}`,
  },
});

const searchButtonContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: backrs.spacing['spacing-xl'],
  flexShrink: 0,
});

const filterRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-xl'],
});

const filterLeftSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
  flexWrap: 'wrap',
});

const collegeDropdownSx: SxProps<Theme> = ({ backrs }) => ({
  minWidth: 160,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  borderRadius: backrs.radius['radius-md'],
  boxShadow: backrs.shadows['shadow-xs'],
  transition: 'all 0.2s ease',
  '& .MuiOutlinedInput-notchedOutline': {
    border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
    transition: 'all 0.2s ease',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  },
  '& .MuiSelect-icon': {
    display: 'none', // Hide default icon - we render our own
  },
});

const menuPaperPropsSx: SxProps<Theme> = ({ backrs }) => ({
  mt: backrs.spacing['spacing-md'],
  boxShadow: backrs.shadows['shadow-xl'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-primary']}`,
  borderRadius: backrs.radius['radius-md'],
});

const menuItemContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
  width: '100%',
});

const menuItemCheckboxSx =
  (isChecked: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '16px',
    height: '16px',
    backgroundColor: isChecked ? backrs.colors['Colors/Background/bg-brand-solid'] : 'transparent',
    borderRadius: backrs.radius['radius-xs'],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: isChecked ? `1px solid ${backrs.colors['Colors/Base/Black']}` : 'none',
  });

const dropdownRenderValueSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
  marginRight: backrs.spacing['spacing-md'],
});

const filterChipSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  borderRadius: backrs.radius['radius-full'],
  paddingX: backrs.spacing['spacing-lg'],
  paddingY: backrs.spacing['spacing-sm'],
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-sm'],
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
  },
});

const filterChipTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const sortByRenderValueSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
  marginRight: backrs.spacing['spacing-md'],
});

const sortByTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const sortByDropdownSx: SxProps<Theme> = ({ backrs }) => ({
  minWidth: 140,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  borderRadius: backrs.radius['radius-md'],
  boxShadow: backrs.shadows['shadow-xs'],
  transition: 'all 0.2s ease',
  '& .MuiOutlinedInput-notchedOutline': {
    border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
    transition: 'all 0.2s ease',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  },
  '& .MuiSelect-icon': {
    display: 'none', // Hide default icon - we render our own
  },
});

const sortMenuItemContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
  width: '100%',
});

const sortMenuItemCheckboxSx =
  (isChecked: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '16px',
    height: '16px',
    backgroundColor: isChecked ? backrs.colors['Colors/Background/bg-brand-solid'] : 'transparent',
    borderRadius: backrs.radius['radius-xs'],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: isChecked ? `1px solid ${backrs.colors['Colors/Base/Black']}` : 'none',
  });

const sortControlsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});

const orderToggleContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
  borderRadius: backrs.radius['radius-md'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  padding: '2px',
});

const orderToggleButtonSx =
  (isActive: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    paddingX: backrs.spacing['spacing-lg'],
    paddingY: backrs.spacing['spacing-sm'],
    borderRadius: backrs.radius['radius-sm'],
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: isActive ? backrs.colors['Colors/Background/bg-primary'] : 'transparent',
    boxShadow: isActive ? backrs.shadows['shadow-xs'] : 'none',
    '& .MuiTypography-root': {
      color: isActive
        ? backrs.colors['Colors/Text/text-primary']
        : backrs.colors['Colors/Text/text-tertiary'],
    },
    '&:hover': {
      backgroundColor: isActive
        ? backrs.colors['Colors/Background/bg-primary']
        : backrs.colors['Colors/Background/bg-primary-hover'],
    },
  });
